import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Target, Zap, Heart, ArrowRight } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">About FoundrScore</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            We believe business plans are dead. The future belongs to founders 
            who test, learn, and adapt faster than everyone else.
          </p>
        </div>

        {/* Mission */}
        <Card className="mb-12" data-testid="about-mission">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              FoundrScore exists to help entrepreneurs and SME founders validate business ideas 
              quickly, without wasting months on lengthy business plans that become outdated 
              the moment they're written.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mt-4">
              We provide clear frameworks, honest scoring, and actionable next steps — 
              so you can go from idea to market test in days, not months.
            </p>
          </CardContent>
        </Card>

        {/* Values */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card data-testid="about-value-1">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Clarity Over Complexity</h3>
              <p className="text-sm text-muted-foreground">
                We cut through the noise. No jargon, no fluff — just clear, 
                actionable insights you can use today.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="about-value-2">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-semibold mb-2">Speed Over Perfection</h3>
              <p className="text-sm text-muted-foreground">
                Done is better than perfect. We help you test and iterate fast, 
                not plan endlessly.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="about-value-3">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Founder-First</h3>
              <p className="text-sm text-muted-foreground">
                We've been in your shoes. Everything we build is designed to 
                help founders succeed.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Team/Story Placeholder */}
        <Card className="mb-12">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Our Story</h2>
            <p className="text-muted-foreground leading-relaxed">
              FoundrScore was born from frustration. We watched too many talented founders 
              spend months crafting beautiful business plans, only to discover their idea 
              didn't resonate with customers.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We built FoundrScore to change that. Our tools are based on proven validation 
              frameworks used by successful startups and accelerators worldwide — distilled 
              into simple, actionable assessments anyone can complete in under 15 minutes.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Whether you're a first-time founder or a serial entrepreneur, FoundrScore 
              gives you the clarity and confidence to test your ideas faster.
            </p>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Validate Your Idea?</h2>
          <p className="text-muted-foreground mb-6">
            Join thousands of founders who test before they build.
          </p>
          <Link to="/assessment">
            <Button size="lg" className="rounded-full px-8">
              Start Full Assessment
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
